import { Component, inject, OnDestroy } from '@angular/core';
import {
  BADGE_STATUS_COLORS,
  BadgeDirective,
  BadgePosition,
  BadgeSize,
  IBadgeConfig,
} from '@ra-web-tech-ui-toolkit/components';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { of, Subscription } from 'rxjs';
import { EXPIRING_SOON_THRESHOLD_DAYS } from '../../../common-constants';
import { CustomDatePipe } from '../../../pipes/date/custom-date.pipe';
import { GRID_PREFERRED_LANGUAGE$ } from '../../grid.tokens';

/**
 * Shape of the row data this renderer needs in order to decide whether an
 * "Expiring Soon" badge is eligible for the current value.
 */
export interface AgTimeFormatRendererData {
  expiresOn?: string | number | Date | null;
  expiryDate?: string | number | Date | null;
}

export interface AgTimeFormatRendererParams<TData = unknown> extends ICellRendererParams<TData> {
  /** Enables the "Expiring Soon" badge for this column. Defaults to false. */
  showExpireSoonFlag?: boolean;
  /**
   * Consumer-supplied predicate deciding whether the current row is eligible
   * for the "Expiring Soon" badge (e.g. based on its status). Application
   * status semantics live outside this renderer.
   */
  isExpiringSoonEligible?: (data: TData | null | undefined) => boolean;
}

@Component({
  styleUrls: ['./ag-time-format.component.scss'],
  templateUrl: './ag-time-format.component.html',
  imports: [BadgeDirective, CustomDatePipe],
})
export class AgTimeFormatComponent<TData = unknown> implements ICellRendererAngularComp, OnDestroy {
  private readonly preferredLanguage$ = inject(GRID_PREFERRED_LANGUAGE$, { optional: true }) ?? of('en-US');

  params!: AgTimeFormatRendererParams<TData>;
  preferredLanguage = 'en-US';
  preferredLangSub?: Subscription;
  BADGE_STATUS_COLORS = BADGE_STATUS_COLORS;
  BadgePosition = BadgePosition;
  BadgeSize = BadgeSize;
  badgeConfig: IBadgeConfig = {
    position: BadgePosition.Before,
    color: BADGE_STATUS_COLORS.Warning,
    size: BadgeSize.Indicator,
  };
  showExpireSoonFlag = false;

  private getExpiryDate(data: TData | null | undefined): Date | null {
    const record = data as (AgTimeFormatRendererData & Record<string, unknown>) | null | undefined;
    if (!record) {
      return null;
    }
    // In case of trial, expiry date is stored in expiresOn field and in case of Entitlement and
    // Universal Credits, expiry date is stored in expiryDate field.
    if (record.expiresOn) {
      return new Date(record.expiresOn);
    }
    if (record.expiryDate) {
      return new Date(record.expiryDate);
    }
    return null;
  }

  isExpiringSoon(): boolean {
    if (!this.showExpireSoonFlag) {
      return false;
    }
    const data = this.params?.data;
    if (!this.params?.isExpiringSoonEligible?.(data)) {
      return false;
    }
    const expiresOn = this.getExpiryDate(data);
    if (!expiresOn) {
      return false;
    }
    const currentDate = new Date();
    const daysToExpire = (expiresOn.getTime() - currentDate.getTime()) / (3600 * 24 * 1000);
    return daysToExpire <= EXPIRING_SOON_THRESHOLD_DAYS && currentDate < expiresOn;
  }

  agInit(params: AgTimeFormatRendererParams<TData>): void {
    this.params = params;
    this.showExpireSoonFlag = this.params.showExpireSoonFlag ?? false;
    this.preferredLangSub = this.preferredLanguage$.subscribe((value) => {
      this.preferredLanguage = String(value);
    });
  }

  // Optional - Return true if you want to manage a cell refresh yourself, otherwise return false.
  // If you return false, the grid will remove the component from the DOM and create  a new component in its place
  // with the new values.
  refresh(): boolean {
    return false;
  }

  ngOnDestroy(): void {
    this.preferredLangSub?.unsubscribe();
  }
}
